import React, { useState, useContext } from "react";
import {
  useNavigate,
  Link,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { useLinkedInAuth } from "../context/LinkedInAuthProvider";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { SessionContext } from "../context/sessionContext";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useContext(SessionContext);

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const from = location.state?.from || "/projects";

  const [searchParams] = useSearchParams();

  const [inviteData, setInviteData] = useState(null);
  const { loginWithLinkedIn } = useLinkedInAuth();
  const { socialLogin } = useContext(SessionContext);
  const [loading, setLoading] = useState({ google: false, linkedin: false });
  const [loading1, setLoading1] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    setError("");
    setEmailError("");
    setPasswordError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setEmailError("");
    setPasswordError("");

    if (!formData.email) {
      setEmailError("Email is required");
      return;
    }

    if (!formData.password) {
      setPasswordError("Password is required");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setLoading1(true);

    try {
      const result = await login({
        email: formData.email,
        password: formData.password,
      });

      if (
        result.success === true ||
        (result.data && result.data.isLoggedIn === true)
      ) {
         const role = result.data?.role || result.data?.user?.role;
        if (role === "superadmin") {
          navigate("/superadmin", { replace: true });
        } else {
          window.location.href = from;
        }
        return;
      }
      const apiError =
        result.message ||
        result.error ||
        result.errorMessage ||
        (result.data && result.data.message) ||
        "Invalid email or password";

      const errorType = result.data?.error || "";
      const orgRequired = result.data?.orgRequired || false;
      if (orgRequired === true) {
        const token = result.data?.token || Cookies.get("token");
        let userName = "";

        if (token) {
          try {
            const decoded = jwtDecode(token);
            userName = decoded?.user?.first_name || decoded?.first_name || "";
          } catch (e) {
            console.error("Failed to decode token:", e);
          }
        }
        navigate("/create-org", {
          state: {
            isEmailSignup: false,
            userName: userName || formData.email.split("@")[0],
            fromLogin: true,
            userEmail: formData.email,
          },
        });
        return;
      }

      // Handle other error types
      if (
        errorType === "emailError" ||
        apiError.toLowerCase().includes("email") ||
        apiError.toLowerCase().includes("user not found") ||
        apiError.toLowerCase().includes("not registered") ||
        apiError.toLowerCase().includes("not found")
      ) {
        setEmailError(apiError);
      } else if (
        errorType === "passwordError" ||
        apiError.toLowerCase().includes("password") ||
        apiError.toLowerCase().includes("incorrect") ||
        apiError.toLowerCase().includes("invalid credentials") ||
        apiError.toLowerCase().includes("wrong password")
      ) {
        setPasswordError(apiError);
      } else {
        setError(apiError);
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("Server error. Please try again later.");
    } finally {
      setLoading1(false);
    }
  };

  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async (codeResponse) => {
      setLoading((prev) => ({ ...prev, google: true }));

      try {
        sessionStorage.setItem("pendingSignupFlow", "true");

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/auth/google-code`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code: codeResponse.code }),
          },
        );

        const data = await response.json();

        if (!response.ok || !data.token) {
          throw new Error(data.message || "Google authentication failed");
        }
        const user = data.user || {
          email: data.email,
          name: data.name,
        };

        const loginResult = await socialLogin(data.token, user);
        if (data.orgRequired === false) {
          sessionStorage.removeItem("pendingSignupFlow");
        }
        if (!loginResult.success) {
          throw new Error("Session setup failed");
        }
        if (data.orgRequired === true) {
          let userName = "";

          try {
            const decoded = jwtDecode(data.token);
            userName =
              decoded?.user?.first_name ||
              decoded?.first_name ||
              user?.name ||
              "";
          } catch (e) {
            console.error("JWT decode failed", e);
          }

          navigate("/create-org", {
            state: {
              isEmailSignup: false,
              socialToken: data.token,
              userName,
              fromLogin: true,
            },
          });
        } else {
          const role = data.role || data.user?.role;
          if (role === "superadmin") {
            navigate("/superadmin", { replace: true });
          } else {
            window.location.replace("/projects");
          }
        }
      } catch (error) {
        console.error("Google auth error:", error);
        alert(error.message || "An error occurred. Please try again.");
      } finally {
        sessionStorage.removeItem("pendingSignupFlow");
        setLoading((prev) => ({ ...prev, google: false }));
      }
    },

    onError: (error) => {
      console.error("Google login error:", error);
      sessionStorage.removeItem("pendingSignupFlow");
      setLoading((prev) => ({ ...prev, google: false }));
    },
  });

  // LinkedIn Login
  const handleLinkedInLogin = () => {
    setLoading((prev) => ({ ...prev, linkedin: true }));
    sessionStorage.setItem("pendingSignupFlow", "true");
    localStorage.setItem("linkedin_signup_flow", "true");

    if (inviteData) {
      localStorage.setItem("pendingInviteData", JSON.stringify(inviteData));
    }

    loginWithLinkedIn(inviteData);
  };

  return (
    <div className="page-bg">
      <div className="create-survey-logo">
        <Link to="/">
          <img
            src="/json-media/img/logo.svg"
            alt="GI"
            width={50}
            style={{ padding: 5 }}
          />
        </Link>
      </div>

      <div className="content-center">
        <div className="signup-container">
          <div className="signup-header">
            <h3>Log in</h3>
            <p>Welcome back to Generative Insight!</p>
          </div>
          <form className="signup-form" onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                style={
                  emailError
                    ? {
                        borderColor: "#ef4444",
                        borderWidth: "1px",
                        borderStyle: "solid",
                      }
                    : {}
                }
              />
              {emailError && (
                <span
                  style={{
                    color: "#ef4444",
                    fontSize: "0.875rem",
                    marginTop: "0.25rem",
                    display: "block",
                  }}
                >
                  {emailError}
                </span>
              )}
            </div>
            <div className="form-group password-group">
              <label>Password</label>
              <div className="password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  style={
                    passwordError
                      ? {
                          borderColor: "#ef4444",
                          borderWidth: "1px",
                          borderStyle: "solid",
                        }
                      : {}
                  }
                />
                <span
                  className="toggle-eye"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEye /> : <FaEyeSlash />}
                </span>
              </div>
              {passwordError && (
                <span
                  style={{
                    color: "#ef4444",
                    fontSize: "0.875rem",
                    marginTop: "0.25rem",
                    display: "block",
                  }}
                >
                  {passwordError}
                </span>
              )}
            </div>
            <div className="forgot-password">
              <Link to="/forgot-password">Forgot password?</Link>
            </div>
            {error && (
              <p
                style={{
                  color: "#ef4444",
                  fontSize: "0.875rem",
                  marginTop: "0.5rem",
                  marginBottom: "0.5rem",
                  textAlign: "center",
                }}
              >
                {error}
              </p>
            )}
            <button type="submit" className="signup-btn">
              {loading1 ? "Logging in..." : "Continue"}
            </button>
          </form>
          {/* Divider */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              margin: "1.5rem 0",
              color: "#94a3b8",
              fontSize: "0.875rem",
            }}
          >
            <div style={{ flex: 1, height: "1px", background: "#374151" }}></div>
            <span style={{ padding: "0 1rem" }}>Or log in with</span>
            <div style={{ flex: 1, height: "1px", background: "#374151" }}></div>
          </div>
          <div style={{ display: "flex", gap: "0.75rem" }}>
            <button
              className="social-btn"
              onClick={() => googleLogin()}
              disabled={loading.google || loading.linkedin}
            >
              <span className="icon">
                <img src="/json-media/icons/google.svg" alt="google" />
              </span>
              {loading.google ? "Connecting..." : "Google"}
            </button>
            <button
              className="social-btn"
              onClick={handleLinkedInLogin}
              disabled={loading.google || loading.linkedin}
            >
              <span className="icon">
                <img src="/json-media/icons/linkedin.svg" alt="linkedin" />
              </span>
              {loading.linkedin ? "Connecting..." : "LinkedIn"}
            </button>
          </div>
          <p
            style={{
              marginTop: "1.5rem",
              textAlign: "center",
              fontSize: "0.95rem",
            }}
          >
            Don't have an account?{" "}
            <Link to="/create-survey" style={{ color: "rgb(59, 130, 246)" }}>
              Click Here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;